import { clearAuthStorage, getAuthToken } from './authSession';

export function getApiBaseUrl(): string {
  const w = window as any;

  // Optional runtime injection (best for Render/prod without rebuild)
  const injected =
    typeof w.__API_BASE_URL__ === "string" ? w.__API_BASE_URL__.trim() : "";
  if (injected) return injected.replace(/\/+$/, "");

  // Build-time override (CRA env var) — useful when the FE/BE split is fixed at build time.
  const fromEnv =
    typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE_URL
      ? String(process.env.REACT_APP_API_BASE_URL).trim()
      : "";
  if (fromEnv) return fromEnv.replace(/\/+$/, "");

  const origin = window.location.origin;

  // ✅ Local dev: any localhost port (CRA auto-bumps to 3001/3002/3003 if 3000 is taken).
  // The backend listens on PORT from .env (defaults to 3001 for this repo), so always
  // route the API to localhost:3001 in dev.
  const isLocalhost =
    /^https?:\/\/localhost(:\d+)?$/i.test(origin) ||
    /^https?:\/\/127\.0\.0\.1(:\d+)?$/i.test(origin);
  if (isLocalhost) return "http://localhost:3001";

  // ✅ If you host FE+BE on different domains and forget injection,
  // this would be wrong. So keep same-origin as default, but rely on injection for split deployments.
  return origin.replace(/\/+$/, "");
}

// ✅ Keep /api routing consistent with your backend mounting
export const API_BASE_URL = `${getApiBaseUrl()}/api`;

function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/**
 * Pull the human-readable message out of an error response.
 *
 * The API's error handler answers with JSON ({ success, message }), while a few
 * routes still reply in plain text. Throwing the raw body dumped the whole JSON
 * payload — stack traces included — straight into whatever modal was open.
 */
function extractErrorMessage(body: string, status: number): string {
  const text = String(body || "").trim();
  if (!text) return `Request failed with status ${status}`;

  if (text.startsWith("{") || text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text);
      const message =
        parsed?.message ?? parsed?.error?.message ?? (typeof parsed?.error === "string" ? parsed.error : null);
      if (typeof message === "string" && message.trim()) return message.trim();
      // JSON we don't recognise: a raw body is still better than nothing, but
      // never one carrying a stack trace.
      return `Request failed with status ${status}`;
    } catch (_) {
      // Not JSON after all — fall through and use the text as-is.
    }
  }

  return text;
}

// Exported for the few blob downloads that must fetch themselves (this module
// only speaks JSON/text): `if (!res.ok) await handleResponse(res)` gives their
// failures the same `status`/`code`/`reason` and session-expiry handling.
export async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const message = extractErrorMessage(text, res.status);
    const isAuthStatus = res.status === 401 || res.status === 403;
    // The CODE from the envelope, never the prose: this used to substring-match
    // "token expired" / "log in again" out of the message, so a copy edit on
    // the server could log everyone out — or stop logging anyone out. The
    // server stamps TOKEN_EXPIRED (middlewares/auth.js, errorHandler.js).
    let envelopeCode = "";
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed?.code === "string") envelopeCode = parsed.code;
    } catch (_) {
      // not JSON — no code, so not a session expiry we can recognise
    }
    const isTokenExpired = envelopeCode === "TOKEN_EXPIRED";

    if (isAuthStatus && isTokenExpired) {
      try {
        clearAuthStorage();
        window.dispatchEvent(new CustomEvent("session-expired", { detail: { message } }));
      } catch (_) {}
    }

    // The status rides along on the Error so callers can tell an operational
    // refusal (400, with a message written for a person) from a system failure
    // (500, a dropped session) and say something different about each. Plain
    // property rather than an Error subclass: this file compiles to ES5, where
    // `instanceof` on a subclassed Error does not survive.
    const err: any = new Error(message);
    err.status = res.status;
    // Machine-readable code from the server envelope ({success:false, message,
    // code}). Callers switch on err.code, never on message prose — a copy edit
    // to the message must not change behavior.
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed?.code === "string" && parsed.code) err.code = parsed.code;
      // N2 (owner re-audit, 2026-09-25): a sub-reason beside `code` — e.g. a
      // supplier pick's `SUPPLIER_PRICE_NOT_RESTATABLE` carries the SERVER's
      // own `option.lineUnitRefusal` here (`NO_STATED_RATIO`, …) so the caller
      // can word a specific sentence from it (`supplierOptionPrice
      // .supplierPickErrorMessage`'s `refusalCopy`), the same way `code`
      // already survives. Without this, moving a hand-rolled fetch onto this
      // client silently dropped the field the message was built from and every
      // such refusal fell back to a generic sentence.
      if (typeof parsed?.reason === "string" && parsed.reason) err.reason = parsed.reason;
      // Field-level validation errors ride along too ({success:false, message,
      // errors:[{path,msg}|{field,message}]}) so forms can mark the offending
      // inputs. The retired services/api.ts client exposed them under these
      // names; AddUserModal and ClientsPage read them.
      if (Array.isArray(parsed?.errors)) {
        err.validationErrors = parsed.errors;
        err.isValidationError = true;
      } else if (parsed?.errors && typeof parsed.errors === "object") {
        // A refusal's machine-readable facts (e.g. CLIENT_INVOICE_UNDISPATCHED_LINES
        // carries {undispatched, total}) — never parsed out of the message.
        err.details = parsed.errors;
      }
    } catch (_) {}
    throw err;
  }

  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    return (await res.json()) as T;
  }

  const text = await res.text().catch(() => "");
  return text as unknown as T;
}

function logFetchHintOnce(err: unknown) {
  try {
    const origin = window.location.origin || "";
    const isLocal =
      /localhost:3000$/i.test(origin) || /127\.0\.0\.1:3000$/i.test(origin);
    if (!isLocal) return;

    const key = "__api_fetch_hint_logged__";
    if ((window as any)[key]) return;
    (window as any)[key] = true;

    // eslint-disable-next-line no-console
    console.warn(
      "[apiClient] Fetch failed. Check backend http://localhost:3001, CORS, and API_BASE_URL.",
      err
    );
  } catch (_) {}
}

async function doFetch<T>(
  url: string,
  init: RequestInit
): Promise<T> {
  try {
    const res = await fetch(url, {
      ...init,
      // ✅ safe even if you don't use cookies now; required if cookies are ever used
      credentials: "include",
    });
    return handleResponse<T>(res);
  } catch (err) {
    logFetchHintOnce(err);
    throw err;
  }
}

export async function get<T>(path: string, init?: Omit<RequestInit, "method" | "headers" | "body">) {
  const token = getAuthToken();
  const headers: HeadersInit = {
    ...(token && { Authorization: `Bearer ${token}` }),
  };
  return doFetch<T>(`${API_BASE_URL}${path}`, { method: "GET", headers, ...init });
}

export async function patch<T>(path: string, body: unknown) {
  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
}

export async function put<T>(path: string, body: unknown) {
  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
}

/**
 * `init` is optional and additive — existing callers are unchanged.
 *
 * It exists so a caller can pass an AbortSignal. The copilot needs one: the model call has no
 * timeout, so a hung request used to leave the panel stuck with no way out and no way to ask
 * anything else.
 */
export async function postJson<T>(path: string, body: unknown, init?: { signal?: AbortSignal }) {
  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
    ...(init?.signal ? { signal: init.signal } : {}),
  });
}

// `init` is optional and additive, as on postJson: the invoice upload passes an
// AbortSignal so staff can cancel a slow multi-page upload.
export async function postFormData<T>(path: string, formData: FormData, init?: { signal?: AbortSignal }) {
  const token = getAuthToken();
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers, // ✅ DO NOT set Content-Type for FormData
    body: formData,
    ...(init?.signal ? { signal: init.signal } : {}),
  });
}

export async function deleteRequest<T>(path: string) {
  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
}
